import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useReviewQueue } from '../hooks/useReviewQueue';
import type { ReviewMessage } from '../outreachTypes';
import { outreachMessages } from '../outreachMessages';
import { hintLines, wordCount } from './reviewHints';
import { reviewMessages as messages } from './reviewMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Review.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Words that offer money (the client mirror of `pitchLint` `mentionsMoney`; the server checks again). */
const MONEY =
  /(\$\s?\d|€\s?\d|\d\s?(usd|eur|dollars?|euros?)\b|\bpay(ing|ment)?\b|\bfee\b|promotion package|paid placement)/i;

/** The edit of one pitch (design outreach f2): the money check runs on the person's own text too. */
const ReviewEditorComponent: React.FC<{
  accountId: number;
  campaignUuid: string;
  message: ReviewMessage;
  onDone: () => void;
}> = ({ accountId, campaignUuid, message, onDone }) => {
  const intl = useIntl();
  const { edit, approve, busyUuid } = useReviewQueue(accountId, campaignUuid);
  const [subject, setSubject] = useState(message.subject);
  const [body, setBody] = useState(message.body);
  const hints = useMemo(() => hintLines(intl, MONEY.test(body) ? ['mentionsMoney'] : [], []), [body, intl]);
  const isBlocked = hints.length > 0 || body.trim() === '' || subject.trim() === '';
  const hintRows = useMemo(
    () =>
      hints.map((hint) => (
        <div key={hint.key} className={clsx(shared.hint, shared.hintBlock)}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{hint.text}</span>
        </div>
      )),
    [hints]
  );

  const save = () => {
    edit({ messageUuid: message.messageUuid, subject: subject.trim(), body })
      .then((result) => {
        const lint = (result as { lintHints?: string[] } | undefined)?.lintHints ?? [];
        if (lint.includes('mentionsMoney') || lint.includes('missingPitchLink')) {
          onDone();
          return undefined;
        }
        return approve(message.messageUuid).then(onDone);
      })
      .catch(console.error);
  };

  return (
    <div className={styles.text} data-testid="review-editor">
      {message.status === 'needsEdit' && <p className={shared.small}>{intl.formatMessage(messages.needsEdit)}</p>}
      <label className={shared.label} htmlFor={`subject-${message.messageUuid}`}>
        {intl.formatMessage(messages.subjectLabel)}
      </label>
      <input
        id={`subject-${message.messageUuid}`}
        className={shared.input}
        value={subject}
        maxLength={200}
        onChange={(event) => setSubject(event.target.value)}
      />
      <label className={shared.label} htmlFor={`body-${message.messageUuid}`}>
        {intl.formatMessage(messages.bodyLabel)}
      </label>
      <textarea
        id={`body-${message.messageUuid}`}
        className={shared.textarea}
        rows={10}
        value={body}
        maxLength={5000}
        onChange={(event) => setBody(event.target.value)}
      />
      {hintRows}
      <div className={styles.foot}>
        <span className={`${shared.small} ${shared.grow}`}>
          {intl.formatMessage(messages.editing, { count: wordCount(body) })}
        </span>
        <SMLButton size="sm" outline omitCap onClick={onDone}>
          {intl.formatMessage(outreachMessages.cancel)}
        </SMLButton>
        <SMLButton
          color="primary"
          size="sm"
          omitCap
          onClick={save}
          isLoading={busyUuid === message.messageUuid}
          disabled={isBlocked}
        >
          {intl.formatMessage(messages.saveApprove)}
        </SMLButton>
      </div>
    </div>
  );
};

export const ReviewEditor = TypedMemo(ReviewEditorComponent);
